import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { INACTIVITY_TIMEOUT, WARNING_TIMEOUT } from "../utils/constants";
import { useActions } from "../services/hooks/useActions";
import { useAppSelector } from "../services/hooks";
import { isAuthSelector } from "../services/selectors/auth.selectors";
import { isLogoutLocked, forceCleanupAllLocks } from "../utils/logoutLock";
import { isStayAwakeActive, clearStayAwakeMode, STAY_AWAKE_CHANNEL } from "../utils/stayAwakeMode";

const useInactivityTimer = (
  setShowWarning: Dispatch<SetStateAction<boolean>>,
  setShowLogoutModal: Dispatch<SetStateAction<boolean>>
) => {
  const navigate = useNavigate();
  const { logout } = useActions();
  const isAuth = useAppSelector(isAuthSelector);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const warningTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!isAuth) return;

    // Create broadcast channel for cross-tab communication
    channelRef.current = new BroadcastChannel('user_activity');

    const resetTimers = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      setShowWarning(false);

      warningTimerRef.current = setTimeout(() => {
        // Don't show warning or logout while a protected process is running
        // or while "Stay Awake" mode is active
        if (isLogoutLocked() || isStayAwakeActive()) {
          // Recording active or stay-awake ON — reschedule so the timer wakes up again later
          resetTimers();
          return;
        }
        setShowWarning(true);
        // Notify other tabs that warning is shown
        channelRef.current?.postMessage({ type: 'warning_shown' });
      }, WARNING_TIMEOUT);

      timerRef.current = setTimeout(() => {
        // Don't logout while a protected process is running (e.g. recording)
        // or while "Stay Awake" mode is active
        if (isLogoutLocked() || isStayAwakeActive()) {
          // Recording active or stay-awake ON — reschedule so we check again after it ends
          resetTimers();
          return;
        }
        // Notify other tabs about logout BEFORE local cleanup (timing safety)
        channelRef.current?.postMessage({ type: 'logged_out' });
        logout();
        setShowWarning(false);
        setShowLogoutModal(true);
        navigate("/");
      }, INACTIVITY_TIMEOUT);
    };

    const handleActivity = () => {
      resetTimers();
      // Notify other tabs about activity
      channelRef.current?.postMessage({ type: 'activity' });
    };

    // Listen for messages from other tabs
    channelRef.current.onmessage = (event) => {
      switch (event.data.type) {
        case 'activity':
          // Activity in another tab - reset timers in this tab too
          resetTimers();
          break;
        case 'warning_shown':
          setShowWarning(true);
          break;
        case 'warning_dismissed':
          // Warning was dismissed in another tab - dismiss here too
          setShowWarning(false);
          resetTimers();
          break;
        case 'logged_out':
          // Another tab logged out — but don't follow if a protected process is active
          if (isLogoutLocked()) {
            // console.warn('[InactivityTimer] Cross-tab logout blocked — logout lock active');
            break;
          }
          logout();
          setShowWarning(false);
          setShowLogoutModal(true);
          navigate("/");
          break;
        case 'forced_logout':
          // User explicitly confirmed logout from another tab — force logout everywhere
          forceCleanupAllLocks();
          clearStayAwakeMode();
          logout();
          setShowWarning(false);
          setShowLogoutModal(true);
          navigate("/");
          break;
      }
    };

    // Listen for stay-awake mode changes — when toggled ON, reset timers
    // so the pending warning/logout timeouts are rescheduled (and will see
    // the new state when they fire).
    const stayAwakeChannel = new BroadcastChannel(STAY_AWAKE_CHANNEL);
    stayAwakeChannel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'stay_awake_changed') {
        resetTimers();
      }
    };

    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("click", handleActivity);

    resetTimers(); // Initialize timers

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("click", handleActivity);
      channelRef.current?.close();
      stayAwakeChannel.close();
    };
  }, [logout, navigate, setShowWarning, setShowLogoutModal, isAuth]);
};

export default useInactivityTimer;
